// RegisterComponent.js
import React, { useEffect, useRef, useState } from "react";
import { useI18N } from "common/hooks";
import AnalyticsService from "@/services/analytics";
import {
  publicPrivacyUrl,
  publicTermsUrl,
  termsAndConditionsAgreedKey,
} from "common/config";
import Checkbox from "@/components/common/Checkbox";
import WideButton from "@/components/common/WideButton";

interface VerifyTermsAndConditionsProps {
  onClose: () => void;
  title: string;
}

const VerifyTermsAndConditions = ({
  onClose,
  title,
}: VerifyTermsAndConditionsProps) => {
  const i18n = useI18N();
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    AnalyticsService.trackGAPagesHandler(
      "authPopupVerifyTermsAndConditionsOpen"
    );
    AnalyticsService.trackMx("authPopupVerifyTermsAndConditionsOpen", {});
  }, []);

  const onContinue = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupVerifyTermsAndConditionsSubmit"
    );
    AnalyticsService.trackMx("authPopupVerifyTermsAndConditionsSubmit", {});

    const timestamp = new Date().getTime();
    localStorage.setItem(
      termsAndConditionsAgreedKey,
      JSON.stringify({ accepted: true, timestamp })
    );

    //close popup
    onClose();
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <section
      style={{
        height:
          window.innerHeight < 800
            ? `${window.innerHeight - 40}px`
            : `${window.innerHeight - 100}px`,
      }}
      className="relative bg-white dark:bg-primary-900 rounded-xl h-full flex flex-col overflow-y-auto"
      role="none"
    >
      <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0">
        <h1
          className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
          tabIndex={0}
          ref={firstFocusRef} // Attach the ref here
        >
          {title}
        </h1>
      </header>

      <div
        aria-label="Modal Content"
        className="py-3 px-6 md:py-6 md:px-14 flex-1 flex flex-col"
        role="none"
      >
        <div className="flex-1 flex flex-col">
          {/* content comes below this */}
          <h2
            className="desktop-h2-small-bold text-primary-800 dark:text-primary-150 my-[16px]"
            tabIndex={0}
          >
            {i18n.get("beforeYouUseSociability")}
          </h2>
          <div
            className="my-0 md:my-2 text-primary-800 dark:text-primary-150"
            tabIndex={0}
          >
            {i18n.get("byClickingAgree")}
          </div>
          <div className="pb-5">
            <hr />
          </div>

          <div className="flex flex-row items-start">
            <div className="mt-1 mr-2">
              <Checkbox
                label=""
                checked={termsAccepted}
                onCheck={(value) => {
                  setTermsAccepted(value);
                }}
              />
            </div>
            <div className="">
              <span
                className="text-neutral-900 dark:text-primary-100 paragraph-small-medium mr-1"
                tabIndex={0}
              >
                {i18n.get("bySelectingAgreeAndContinue")}
              </span>
              <a
                href={publicTermsUrl}
                target="_blank"
                className="text-[#352FC3] dark:text-primary-300 paragraph-small-medium leading-normal mr-1 underline"
              >
                {i18n.get("termsAndConditions")}
              </a>
              <span
                className=" paragraph-small-medium leading-normal mr-1"
                tabIndex={0}
              >
                {i18n.get("and")}
              </span>
              <a
                href={publicPrivacyUrl}
                target="_blank"
                className="text-[#352FC3] dark:text-primary-300 paragraph-small-medium leading-normal underline"
              >
                {i18n.get("privacyNotice")}
              </a>
            </div>
          </div>
          <div className="flex-1" />
        </div>

        <div className="flex-1" />
        <div className="flex justify-center mb-4">
          <WideButton disabled={!termsAccepted} onClick={() => onContinue()}>
            {i18n.get("continue")}
          </WideButton>
        </div>
      </div>
    </section>
  );
};

export default VerifyTermsAndConditions;
